import { isEqual, isNil, isString, keyBy, last, pick, sortBy } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { omitDeep } from "../omit-deep";
import { ProcessModelCommandEnum } from "../process-things";
import type {
  ProcessModelCopyArea,
  ProcessModelCopyElement,
  ProcessModelCopyModel,
  ProcessModelCopyPhase,
} from "./process-model-copy.model";
import { ProcessModelCopyLevel } from "./process-model-copy.model";
import type {
  ProcessModelCopyPlan,
  ProcessModelCopyPlannedArea,
  ProcessModelCopyPlannedPhase,
  ProcessModelCopyPlannedTask,
  ProcessModelCopyRewire,
} from "./process-model-copy-plan";

/** Why the commands of a copy could not be built. */
export enum ProcessModelCopyCommandsError {
  /** The plan still has blocking reasons, see `ProcessModelCopyPlan.blockingReasons`. */
  NotApplicable = "NotApplicable",
  /** The container, or an existing area or phase the copy goes into, is no longer in the target. */
  TargetElementMissing = "TargetElementMissing",
  /** A task the copy rewires is gone, or waits for other tasks than when the copy was planned. */
  RewiredTaskChanged = "RewiredTaskChanged",
}

/** A target task the plan rewires, as loaded right before the copy: its update writes it back whole. */
export interface ProcessModelCopyRewiredTask extends Record<string, unknown> {
  id: string;
  _etag?: string;
  modelId: string;
  predecessorsIds?: string[];
}

export interface ProcessModelCopyCommandsInput {
  organizationId: string;
  /** The id of the target model. */
  rootId: string;
  /** The model the plan was made for, its areas, phases and tasks with their `id`, in the order of its tree. */
  target: ProcessModelCopyModel;
  /** Every target task of `ProcessModelCopyPlan.rewires`. */
  rewiredTasks: readonly ProcessModelCopyRewiredTask[];
  generateId?: () => string;
}

/** The data of a create command; the copied element plus where it goes. */
export interface ProcessModelCopyCreateData extends ProcessModelCopyElement {
  /** Generated, so later commands of the batch can name the new element as their parent or predecessor. */
  id: string;
  organizationId: string;
  rootId: string;
  parentId: string;
  previousAreaId?: string;
  previousPhaseId?: string;
  previousTaskId?: string;
}

/** One command of a copy, shaped like the GraphQL input `ProcessTaskModelCommandInputType` of `applyCommandBatch`. */
export interface ProcessModelCopyCommand {
  command: ProcessModelCommandEnum;
  organizationId: string;
  rootId: string;
  entityId?: string;
  entityEtag?: string;
  createAreaData?: ProcessModelCopyCreateData;
  createPhaseData?: ProcessModelCopyCreateData;
  createTaskData?: ProcessModelCopyCreateData;
  updateTaskData?: Record<string, unknown>;
}

export type ProcessModelCopyCommandsResult =
  | { commands: ProcessModelCopyCommand[]; error?: never }
  | { commands?: never; error: ProcessModelCopyCommandsError };

interface BuildContext {
  input: ProcessModelCopyCommandsInput;
  generateId: () => string;
}

/** Where created elements go: the parent, and the sibling the first of them follows (none: the parent is empty). */
interface Position {
  parentId: string;
  previousId: string | null;
}

interface PositionedCommands {
  commands: ProcessModelCopyCommand[];
  /** The id of the last element created, which the next sibling follows. */
  lastId: string | null;
}

// The fields an area or phase create input has; the copied element may hold more (e.g. from the source's storage)
const STAGE_INPUT_FIELDS = [
  "_typeName",
  "modelId",
  "displayName",
  "displayDescription",
  "isHidden",
  "doNotCreateOnSync",
  "visibleForRoles",
];

/**
 * The commands that apply a resolved copy plan, in the order `applyCommandBatch` has to run them: the creates in tree
 * order (every area before its phases, every phase before its tasks) with generated ids, each placed after the element
 * before it, and the `UpdateTask` rewires last. The copied elements come as the plan made them: with their final
 * modelIds, references and roles.
 */
export function buildCopyCommands(
  plan: ProcessModelCopyPlan,
  input: ProcessModelCopyCommandsInput
): ProcessModelCopyCommandsResult {
  if (!plan.isApplicable) {
    return { error: ProcessModelCopyCommandsError.NotApplicable };
  }

  const context: BuildContext = { input, generateId: input.generateId ?? uuidv4 };
  const creates = buildCreates(plan, context);
  const rewires = plan.rewires.map((rewire) => buildRewire(rewire, input));
  if (isNil(creates) || rewires.some(isNil)) {
    return {
      error: isNil(creates)
        ? ProcessModelCopyCommandsError.TargetElementMissing
        : ProcessModelCopyCommandsError.RewiredTaskChanged,
    };
  }

  return { commands: [...creates, ...rewires] };
}

function buildCreates(plan: ProcessModelCopyPlan, context: BuildContext): ProcessModelCopyCommand[] | null {
  const { target, rootId } = context.input;
  switch (plan.level) {
    case ProcessModelCopyLevel.Area:
      return createAreas(plan.areas, { parentId: rootId, previousId: getLastId(target.areas) }, context);
    case ProcessModelCopyLevel.Phase: {
      const area = findArea(target, plan.container.modelId);
      return isNil(area) ? null : createPhases(plan.phases, area, context);
    }

    case ProcessModelCopyLevel.Task: {
      const phase = findPhase(target, plan.container.modelId);
      return isNil(phase)
        ? null
        : (createTasks(plan.tasks, getAppendPosition(phase, phase.tasks), context)?.commands ?? null);
    }

    default: {
      const unhandled: never = plan.level;
      throw new Error(`Unknown copy level ${unhandled}`);
    }
  }
}

function createAreas(
  areas: ProcessModelCopyPlannedArea[],
  position: Position | null,
  context: BuildContext
): ProcessModelCopyCommand[] | null {
  if (isNil(position)) {
    return null;
  }

  const created = areas.reduce<PositionedCommands | null>(
    (sofar, plannedArea) => {
      if (isNil(sofar)) {
        return null;
      }

      // An existing area takes the phases at its end and stays where it is
      if (!isNil(plannedArea.existingTargetModelId)) {
        const existing = findArea(context.input.target, plannedArea.existingTargetModelId);
        const phases = isNil(existing) ? null : createPhases(plannedArea.phases, existing, context);
        return isNil(phases) ? null : { commands: [...sofar.commands, ...phases], lastId: sofar.lastId };
      }

      const id = context.generateId();
      const createArea = toCreateCommand(
        ProcessModelCommandEnum.CreateArea,
        pick(plannedArea.area, STAGE_INPUT_FIELDS),
        {
          id,
          position: { parentId: position.parentId, previousId: sofar.lastId },
          previousKey: "previousAreaId",
          context,
        }
      );
      const phases = createPhasesAt(plannedArea.phases, { parentId: id, previousId: null }, context);
      return isNil(phases) ? null : { commands: [...sofar.commands, createArea, ...phases.commands], lastId: id };
    },
    { commands: [], lastId: position.previousId }
  );
  return created?.commands ?? null;
}

function createPhases(
  phases: ProcessModelCopyPlannedPhase[],
  area: ProcessModelCopyArea,
  context: BuildContext
): ProcessModelCopyCommand[] | null {
  const position = getAppendPosition(area, area.phases);
  return isNil(position) ? null : (createPhasesAt(phases, position, context)?.commands ?? null);
}

function createPhasesAt(
  phases: ProcessModelCopyPlannedPhase[],
  position: Position,
  context: BuildContext
): PositionedCommands | null {
  return phases.reduce<PositionedCommands | null>(
    (sofar, plannedPhase) => {
      if (isNil(sofar)) {
        return null;
      }

      // An existing phase takes the tasks at its end and stays where it is
      if (!isNil(plannedPhase.existingTargetModelId)) {
        const existing = findPhase(context.input.target, plannedPhase.existingTargetModelId);
        const taskPosition = isNil(existing) ? null : getAppendPosition(existing, existing.tasks);
        if (isNil(taskPosition)) {
          return null;
        }

        const tasks = createTasks(plannedPhase.tasks, taskPosition, context);
        return { commands: [...sofar.commands, ...tasks.commands], lastId: sofar.lastId };
      }

      const id = context.generateId();
      const createPhase = toCreateCommand(
        ProcessModelCommandEnum.CreatePhase,
        pick(plannedPhase.phase, STAGE_INPUT_FIELDS),
        {
          id,
          position: { parentId: position.parentId, previousId: sofar.lastId },
          previousKey: "previousPhaseId",
          context,
        }
      );
      const tasks = createTasks(plannedPhase.tasks, { parentId: id, previousId: null }, context);
      return { commands: [...sofar.commands, createPhase, ...tasks.commands], lastId: id };
    },
    { commands: [], lastId: position.previousId }
  );
}

function createTasks(
  tasks: ProcessModelCopyPlannedTask[],
  position: Position | null,
  context: BuildContext
): PositionedCommands | null {
  if (isNil(position)) {
    return null;
  }

  return tasks.reduce<PositionedCommands>(
    (sofar, { task }) => {
      const id = context.generateId();
      const createTask = toCreateCommand(ProcessModelCommandEnum.CreateTask, task, {
        id,
        position: { parentId: position.parentId, previousId: sofar.lastId },
        previousKey: "previousTaskId",
        context,
      });
      return { commands: [...sofar.commands, createTask], lastId: id };
    },
    { commands: [], lastId: position.previousId }
  );
}

interface CreateOptions {
  id: string;
  position: Position;
  previousKey: "previousAreaId" | "previousPhaseId" | "previousTaskId";
  context: BuildContext;
}

function toCreateCommand(
  command:
    | ProcessModelCommandEnum.CreateArea
    | ProcessModelCommandEnum.CreatePhase
    | ProcessModelCommandEnum.CreateTask,
  element: Partial<ProcessModelCopyElement>,
  { id, position, previousKey, context }: CreateOptions
): ProcessModelCopyCommand {
  const { organizationId, rootId } = context.input;
  const data: ProcessModelCopyCreateData = {
    ...element,
    modelId: element.modelId,
    id,
    organizationId,
    rootId,
    parentId: position.parentId,
    // Without a previous element the new one is the first of its parent, which then has no other children yet
    ...(isNil(position.previousId) ? {} : { [previousKey]: position.previousId }),
  };
  return toCommand(command, context.input, toCreateFields(command, data));
}

function toCreateFields(
  command:
    | ProcessModelCommandEnum.CreateArea
    | ProcessModelCommandEnum.CreatePhase
    | ProcessModelCommandEnum.CreateTask,
  data: ProcessModelCopyCreateData
): Partial<ProcessModelCopyCommand> {
  switch (command) {
    case ProcessModelCommandEnum.CreateArea:
      return { createAreaData: data };
    case ProcessModelCommandEnum.CreatePhase:
      return { createPhaseData: data };
    case ProcessModelCommandEnum.CreateTask:
      return { createTaskData: data };
    default: {
      const unhandled: never = command;
      throw new Error(`Unknown create command ${unhandled}`);
    }
  }
}

/** The update that makes a target task wait for the copied tasks; `null` when the task changed since the plan. */
function buildRewire(
  { taskModelId, previousPredecessorsIds, predecessorsIds }: ProcessModelCopyRewire,
  input: ProcessModelCopyCommandsInput
): ProcessModelCopyCommand | null {
  const task = keyBy(input.rewiredTasks, ({ modelId }) => modelId)[taskModelId];
  const isUnchanged = !isNil(task) && isEqual(sortBy(task.predecessorsIds ?? []), sortBy(previousPredecessorsIds));
  if (!isUnchanged) {
    return null;
  }

  // Loaded through GraphQL, the task may carry __typename, which no input has
  const updateTaskData: Record<string, unknown> = { ...omitDeep(task, ["__typename"]), predecessorsIds };
  return toCommand(ProcessModelCommandEnum.UpdateTask, input, {
    entityId: task.id,
    entityEtag: task._etag,
    updateTaskData,
  });
}

function toCommand(
  command: ProcessModelCommandEnum,
  { organizationId, rootId }: ProcessModelCopyCommandsInput,
  fields: Partial<ProcessModelCopyCommand>
): ProcessModelCopyCommand {
  return { command, organizationId, rootId, ...fields };
}

/** Appending to a container: after its last child, or as its first one. `null` when the container has no id. */
function getAppendPosition(
  container: ProcessModelCopyElement,
  children: readonly ProcessModelCopyElement[] | undefined
): Position | null {
  const parentId = getId(container);
  if (isNil(parentId)) {
    return null;
  }

  return { parentId, previousId: getLastId(children) };
}

function getLastId(elements: readonly ProcessModelCopyElement[] | undefined): string | null {
  const lastElement = last(elements ?? []);
  return isNil(lastElement) ? null : getId(lastElement);
}

function getId({ id }: ProcessModelCopyElement): string | null {
  return isString(id) ? id : null;
}

function findArea(target: ProcessModelCopyModel, modelId: string): ProcessModelCopyArea | null {
  return (target.areas ?? []).find((area) => area.modelId === modelId) ?? null;
}

function findPhase(target: ProcessModelCopyModel, modelId: string): ProcessModelCopyPhase | null {
  return (target.areas ?? []).flatMap(({ phases }) => phases ?? []).find((phase) => phase.modelId === modelId) ?? null;
}
